import { useEffect, useRef, useState, useSyncExternalStore, type FormEvent } from 'react';
import { api } from './api';
import { getAlbumSignupDetails, getContributorName, hasAlbumSignup, rememberAlbumSignup, subscribeAlbumSignup, type AlbumSignupDetails } from './albumSignup';

export function AlbumRequest({ initiallyOpen = false }: { initiallyOpen?: boolean }) {
  const [open, setOpen] = useState(initiallyOpen);
  const [name, setName] = useState(getContributorName);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const sent = useSyncExternalStore(subscribeAlbumSignup, hasAlbumSignup, () => false);
  const saved = useSyncExternalStore(subscribeAlbumSignup, getAlbumSignupDetails, () => null);
  const [editing, setEditing] = useState<AlbumSignupDetails | null>(null);
  const [updated, setUpdated] = useState(false);
  const emailInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');
  useEffect(() => { if (editing) emailInput.current?.focus(); }, [editing]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const submittedName = name.trim();
      const submittedEmail = email.trim().toLowerCase();
      const result = editing
        ? await api.correctAlbumSignup(submittedName, submittedEmail, editing.email)
        : await api.requestAlbum(submittedName, submittedEmail);
      rememberAlbumSignup({ name: submittedName, email: submittedEmail, canEdit: result.canEdit });
      setUpdated(Boolean(editing));
      setEditing(null);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Please try again.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="album-request" aria-label="Request the photo album">
      {sent && !editing ? (
        <>
          <h3>{updated ? 'Email updated.' : 'You’re on the list.'}</h3>
          <p role="status">
            {saved ? <>We’ll email the finished album to <strong className="signup-email-address">{saved.email}</strong> when it’s ready.</>
              : 'We’ll email you the finished album when it’s ready.'}
          </p>
          {saved?.canEdit ? <button className="secondary-button full-width" onClick={() => {
            setName(saved.name); setEmail(saved.email); setEditing(saved); setOpen(true); setError('');
          }}>Change email</button> : <p className="signup-help">Need to change your email? Please ask the family.</p>}
        </>
      ) : (
        <>
          <h3>{editing ? 'Change your email.' : 'Get the finished album.'}</h3>
          <p>
            {editing ? 'Check your address below. We’ll send the finished album to your updated email when it’s ready.'
              : 'We’re collecting photos until 28 October. Sign up and we’ll email you the finished album when it’s ready.'}
          </p>
          {!open ? (
            <button className="secondary-button" onClick={() => setOpen(true)}>
              Sign me up
            </button>
          ) : (
            <form onSubmit={(event) => void submit(event)}>
              <label>
                Your name
                <input
                  type="text"
                  autoComplete="name"
                  maxLength={80}
                  value={name}
                  disabled={busy}
                  onChange={(event) => setName(event.target.value)}
                  required
                />
              </label>
              <label>
                Email address
                <input
                  type="email"
                  ref={emailInput}
                  autoComplete="email"
                  maxLength={254}
                  value={email}
                  disabled={busy}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>
              <label className="album-consent">
                <input type="checkbox" required defaultChecked={Boolean(editing)} disabled={busy} />
                Email me the album download link. My details will only be used for this request.
              </label>
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}
              <button className="button" disabled={busy}>
                {busy ? 'Saving…' : editing ? 'Save email' : 'Sign me up'}
              </button>
              {editing && <button className="text-button signup-cancel" type="button" disabled={busy} onClick={() => {
                setEditing(null); setError('');
              }}>Keep current email</button>}
            </form>
          )}
        </>
      )}
    </section>
  );
}
